import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Plus,
  X,
  Sparkles,
  Upload,
  Loader2,
  Check,
  AlertCircle,
  Copy,
} from "lucide-react";
import SearchableSelect, { SelectOption } from "../../ui/SearchableSelect";
import QuickCreateMatterModal from "../../ui/QuickCreateMatterModal";
import { mattersApi, documentsApi, esignTemplatesApi } from "../../../services/api";

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"];
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ─── Types ────────────────────────────────────────────────────────────────────

interface DocumentEntry {
  documentId: string;
  title: string;
  requiresSignature: boolean;
}

interface WizardData {
  matterId: string;
  templateId: string;
  subject: string;
  message: string;
  documents: DocumentEntry[];
  signers: any[];
  fields: any[];
  sequentialSigning: boolean;
  expirationDays: number;
  reminders: { enabled: boolean; intervalDays: number; maxReminders: number };
  saveAsTemplate: boolean;
  templateName: string;
}

interface DocumentsStepProps {
  data: WizardData;
  onChange: (data: WizardData) => void;
}

interface UploadState {
  file: File | null;
  uploading: boolean;
  progress: number;
  error: string;
  success: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function isValidFileType(file: File): boolean {
  if (ACCEPTED_MIME_TYPES.includes(file.type)) return true;
  const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
  return ACCEPTED_EXTENSIONS.includes(ext);
}

// ─── Component ────────────────────────────────────────────────────────────────

const DocumentsStep: React.FC<DocumentsStepProps> = ({ data, onChange }) => {
  const [matterOptions, setMatterOptions] = useState<SelectOption[]>([]);
  const [templateOptions, setTemplateOptions] = useState<SelectOption[]>([]);
  const [documentOptions, setDocumentOptions] = useState<SelectOption[]>([]);
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");

  // Keep a ref to the latest data so async callbacks never use stale closures
  const dataRef = useRef(data);
  dataRef.current = data;

  // Quick create matter
  const [showCreateMatter, setShowCreateMatter] = useState(false);

  // Upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [upload, setUpload] = useState<UploadState>({
    file: null,
    uploading: false,
    progress: 0,
    error: "",
    success: false,
  });

  // Fetch matters
  useEffect(() => {
    setLoadingMatters(true);
    mattersApi
      .list({ limit: 100 })
      .then((res) => {
        const matters = res.data?.data || res.data || [];
        setMatterOptions(
          matters.map((m: any) => ({
            value: m._id,
            label: m.title || m.name || m._id,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingMatters(false));
  }, []);

  const refreshMatters = useCallback(() => {
    mattersApi.list({ limit: 100 }).then((res) => {
      const matters = res.data?.data || res.data || [];
      setMatterOptions(
        matters.map((m: any) => ({ value: m._id, label: m.title || m.name || m._id }))
      );
    }).catch(() => {});
  }, []);

  // Fetch templates
  useEffect(() => {
    setLoadingTemplates(true);
    esignTemplatesApi
      .list()
      .then((res) => {
        const templates = res.data?.data || res.data || [];
        setTemplateOptions(
          templates.map((t: any) => ({
            value: t._id,
            label: t.name || t._id,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingTemplates(false));
  }, []);

  // Fetch documents when matter changes (or all firm docs if no matter)
  useEffect(() => {
    setLoadingDocs(true);
    const params: Record<string, any> = { limit: 100 };
    if (data.matterId) {
      params.matterId = data.matterId;
    }
    documentsApi
      .list(params)
      .then((res) => {
        const docs = res.data?.data || res.data || [];
        setDocumentOptions(
          docs.map((d: any) => ({
            value: d._id,
            label: d.title || d.originalName || d.fileName || d._id,
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingDocs(false));
  }, [data.matterId]);

  // Handle template selection
  const handleTemplateChange = async (templateId: string | null) => {
    if (!templateId) {
      setSelectedTemplateName("");
      onChange({ ...data, templateId: "" });
      return;
    }

    try {
      const res = await esignTemplatesApi.get(templateId);
      const template = res.data?.data || res.data;
      setSelectedTemplateName(template.name || "");

      const SIGNER_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

      // Pre-fill from template defaults
      const updates: Partial<WizardData> = {
        templateId,
        subject: template.defaults?.subject || data.subject,
        message: template.defaults?.message || data.message,
        sequentialSigning: template.defaults?.sequentialSigning ?? data.sequentialSigning,
        expirationDays: template.defaults?.expirationDays ?? data.expirationDays,
        reminders: {
          enabled: template.defaults?.remindersEnabled ?? data.reminders.enabled,
          intervalDays: template.defaults?.reminderIntervalDays ?? data.reminders.intervalDays,
          maxReminders: data.reminders.maxReminders,
        },
      };

      // Pre-fill signers from template
      if (template.defaultSigners?.length > 0) {
        updates.signers = template.defaultSigners.map((s: any, i: number) => ({
          name: "",
          email: "",
          role: s.role || "client",
          order: s.order || i + 1,
          color: SIGNER_COLORS[i % SIGNER_COLORS.length],
          clientId: undefined,
        }));
      }

      onChange({ ...data, ...updates });
    } catch {
      onChange({ ...data, templateId });
    }
  };

  // Document management
  const addDocument = () => {
    onChange({
      ...data,
      documents: [
        ...data.documents,
        { documentId: "", title: "", requiresSignature: true },
      ],
    });
  };

  const removeDocument = (index: number) => {
    if (data.documents.length <= 1) return;
    onChange({
      ...data,
      documents: data.documents.filter((_, i) => i !== index),
    });
  };

  const updateDocument = (
    index: number,
    field: keyof DocumentEntry,
    value: any
  ) => {
    const updated = [...data.documents];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...data, documents: updated });
  };

  const handleDocumentSelect = (index: number, docId: string | null) => {
    const updated = [...data.documents];
    if (!docId) {
      updated[index] = { ...updated[index], documentId: "", title: "" };
    } else {
      const doc = documentOptions.find((o) => o.value === docId);
      updated[index] = {
        ...updated[index],
        documentId: docId,
        title: doc?.label || "",
      };
    }
    onChange({ ...data, documents: updated });
  };

  // ── Upload handlers ────────────────────────────────────────────────────────

  const validateFile = useCallback((file: File): string | null => {
    if (!isValidFileType(file)) {
      return `Invalid file type. Please upload a PDF, DOC, or DOCX file.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File is too large (${formatFileSize(file.size)}). Maximum size is ${MAX_FILE_SIZE_MB} MB.`;
    }
    return null;
  }, []);

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      const validationError = validateFile(selectedFile);
      if (validationError) {
        setUpload((prev) => ({ ...prev, error: validationError, file: null }));
        return;
      }
      // Set the file and immediately trigger upload
      setUpload({
        file: selectedFile,
        uploading: true,
        progress: 0,
        error: "",
        success: false,
      });

      // Start upload immediately
      const formData = new FormData();
      formData.append("file", selectedFile);
      const titleFromFile = selectedFile.name.replace(/\.[^/.]+$/, "");
      formData.append("title", titleFromFile);
      const currentMatterId = dataRef.current.matterId;
      if (currentMatterId) {
        formData.append("matterId", currentMatterId);
      }
      formData.append("type", "other");

      const progressInterval = setInterval(() => {
        setUpload((prev) => ({
          ...prev,
          progress: Math.min(prev.progress + 12, 90),
        }));
      }, 200);

      documentsApi
        .upload(formData)
        .then((res) => {
          clearInterval(progressInterval);
          setUpload((prev) => ({ ...prev, progress: 100, success: true, uploading: false }));

          const newDoc = res.data?.data || res.data;
          const newDocId = newDoc?._id;
          const newDocTitle = newDoc?.title || newDoc?.originalName || titleFromFile;

          if (newDocId) {
            const newEntry: DocumentEntry = {
              documentId: newDocId,
              title: newDocTitle,
              requiresSignature: true,
            };

            setDocumentOptions((prev) => [
              ...prev,
              { value: newDocId, label: newDocTitle },
            ]);

            const currentData = dataRef.current;
            const emptyIndex = currentData.documents.findIndex((d) => !d.documentId);
            if (emptyIndex >= 0) {
              const updated = [...currentData.documents];
              updated[emptyIndex] = newEntry;
              onChange({ ...currentData, documents: updated });
            } else {
              onChange({
                ...currentData,
                documents: [...currentData.documents, newEntry],
              });
            }
          }

          setTimeout(() => {
            clearUpload();
          }, 1500);
        })
        .catch((err: any) => {
          clearInterval(progressInterval);
          setUpload((prev) => ({
            ...prev,
            uploading: false,
            progress: 0,
            error:
              err.response?.data?.error ||
              err.response?.data?.message ||
              "Upload failed. Please try again.",
          }));
        });
    },
    [validateFile, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files?.[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const clearUpload = () => {
    setUpload({
      file: null,
      uploading: false,
      progress: 0,
      error: "",
      success: false,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5">
      {/* Matter Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Matter
          <span className="text-xs text-slate-400 ml-1 font-normal">(Optional)</span>
        </label>
        <SearchableSelect
          options={matterOptions}
          value={data.matterId || null}
          onChange={(val) => onChange({ ...data, matterId: val || "" })}
          placeholder="Select a matter (optional)"
          searchPlaceholder="Search matters..."
          loading={loadingMatters}
          clearable
          addNew={{ label: "Create new matter", onAdd: () => setShowCreateMatter(true) }}
        />
        <p className="text-xs text-slate-400 mt-1">Optionally link this signing request to a matter</p>
      </div>

      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Template
          <span className="text-xs text-slate-400 ml-1 font-normal">(optional)</span>
        </label>
        {templateOptions.length === 0 && !loadingTemplates ? (
          <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
            <Copy className="h-5 w-5 text-slate-300 flex-shrink-0" />
            <div>
              <p className="text-sm text-slate-500">No templates yet</p>
              <p className="text-xs text-slate-400">Save a signing request as a template to reuse it later</p>
            </div>
          </div>
        ) : (
          <SearchableSelect
            options={templateOptions}
            value={data.templateId || null}
            onChange={handleTemplateChange}
            placeholder="Select a template to auto-fill..."
            searchPlaceholder="Search templates..."
            loading={loadingTemplates}
            clearable
          />
        )}
        {data.templateId && selectedTemplateName && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
            <Sparkles className="h-3 w-3" />
            Using template: {selectedTemplateName}
          </div>
        )}
      </div>

      {/* Email Subject */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Email Subject <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.subject}
          onChange={(e) => onChange({ ...data, subject: e.target.value })}
          placeholder="E.g., Retainer Agreement for Smith v. Jones"
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
        />
        <p className="text-xs text-slate-400 mt-1">Subject line of the signing invitation email sent to signers</p>
      </div>

      {/* Email Message */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Email Message
          <span className="text-xs text-slate-400 ml-1 font-normal">(optional)</span>
        </label>
        <textarea
          value={data.message}
          onChange={(e) => onChange({ ...data, message: e.target.value })}
          placeholder="Add a personal note to include in the signing invitation email..."
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
        />
      </div>

      {/* Documents */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
            <FileText className="h-4 w-4" />
            Documents <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={() => {
              if (!upload.uploading) fileInputRef.current?.click();
            }}
            className="flex items-center gap-1 text-xs font-medium transition-colors"
            style={{ color: "var(--color-primary)" }}
          >
            <Upload className="h-3.5 w-3.5" />
            Upload Document
          </button>
        </div>

        {/* ── Upload Document Zone ──────────────────────────────────── */}
        <div className="mb-4">

          {/* Drag-and-drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => {
              if (!upload.uploading) fileInputRef.current?.click();
            }}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
              isDragOver
                ? "border-emerald-500 bg-emerald-50/60"
                : upload.file
                  ? "border-emerald-400 bg-emerald-50/30"
                  : "border-slate-200 hover:border-emerald-400 hover:bg-slate-50"
            } ${upload.uploading ? "pointer-events-none opacity-80" : ""}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => {
                if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
              }}
            />

            {upload.success ? (
              /* ── Success state ── */
              <div className="flex flex-col items-center gap-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#0F5132" }}
                >
                  <Check className="h-5 w-5 text-white" />
                </div>
                <p className="text-sm font-medium text-emerald-700">
                  Document uploaded and added
                </p>
              </div>
            ) : upload.uploading && upload.file ? (
              /* ── Uploading state ── */
              <div className="flex items-center justify-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "rgba(15, 81, 50, 0.08)" }}
                >
                  <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0F5132" }} />
                </div>
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {upload.file.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    Uploading... {upload.progress}%
                  </p>
                </div>
              </div>
            ) : (
              /* ── Empty state — ready for drop/click ── */
              <>
                <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 font-medium">
                  Drop your file here, or click to browse
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  PDF, DOC, DOCX up to {MAX_FILE_SIZE_MB} MB
                </p>
              </>
            )}
          </div>

          {/* Upload progress bar */}
          {upload.uploading && (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Uploading...
                </span>
                <span>{upload.progress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1.5">
                <motion.div
                  className="h-1.5 rounded-full"
                  style={{ backgroundColor: "#0F5132" }}
                  initial={{ width: 0 }}
                  animate={{ width: `${upload.progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}

          {/* Upload error */}
          {upload.error && (
            <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{upload.error}</p>
            </div>
          )}

        </div>

        {/* ── Divider ── */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 h-px bg-slate-300" />
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
            or select from existing documents
          </span>
          <div className="flex-1 h-px bg-slate-300" />
        </div>

        {/* ── Document rows ── */}
        <div className="space-y-3">
          {data.documents.map((doc, i) => (
            <div
              key={i}
              className={`rounded-xl p-3 transition-colors ${
                doc.documentId
                  ? "bg-emerald-50/60 border-2 border-emerald-400"
                  : "bg-slate-50 border border-slate-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <SearchableSelect
                    options={documentOptions}
                    value={doc.documentId || null}
                    onChange={(val) => handleDocumentSelect(i, val)}
                    placeholder="Select a document..."
                    searchPlaceholder="Search documents..."
                    loading={loadingDocs}
                    variant={doc.documentId ? "success" : "default"}
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={doc.requiresSignature}
                      onChange={(e) =>
                        updateDocument(i, "requiresSignature", e.target.checked)
                      }
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Signature required
                  </label>
                  {data.documents.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDocument(i)}
                      className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {data.documents.length === 0 && (
          <button
            type="button"
            onClick={addDocument}
            className="w-full border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-slate-300 transition-colors"
          >
            <FileText className="h-6 w-6 text-slate-400 mx-auto mb-2" />
            <span className="text-sm text-slate-500">
              Click to add a document
            </span>
          </button>
        )}

        {/* Add Another Document button — below rows */}
        <button
          type="button"
          onClick={addDocument}
          className="mt-3 flex items-center gap-1.5 text-xs font-medium transition-colors hover:opacity-80"
          style={{ color: "var(--color-primary)" }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Another Document
        </button>
      </div>

      {/* Quick Create Matter Modal */}
      <QuickCreateMatterModal
        open={showCreateMatter}
        onClose={() => setShowCreateMatter(false)}
        onCreated={(matter: any) => {
          onChange({ ...data, matterId: matter._id });
          refreshMatters();
          setShowCreateMatter(false);
        }}
      />
    </div>
  );
};

export default DocumentsStep;
